import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSalePage } from '../../contexts/SalePageContext';
import SalePageNavbar from '../../components/SalePageNavbar';
import QRPromptPayPayment from '../../components/QRPromptPayPayment';
import { Calendar, ArrowLeft, Check, Crown, Zap, Mail, MessageCircle, Bell, Clock, Sparkles } from 'lucide-react';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';

type BookingTier = 'basic' | 'pro' | 'unlimited';
type BillingCycle = 'monthly' | '6months' | 'yearly';

interface PricingTier {
  id: BookingTier;
  name: string;
  bookingsLimit: number;
  monthlyPrice: number;
  sixMonthsPrice: number;
  yearlyPrice: number;
  features: string[];
  recommended?: boolean;
}

const pricingTiers: PricingTier[] = [
  {
    id: 'basic',
    name: 'Basic Queue',
    bookingsLimit: 50,
    monthlyPrice: 99,
    sixMonthsPrice: 495,
    yearlyPrice: 990,
    features: [
      '50 การจองต่อเดือน',
      'Dashboard Notification',
      'ตั้งค่าเวลาทำงาน',
      'จัดการคิวพื้นฐาน',
      'ประวัติการจอง 30 วัน'
    ]
  },
  {
    id: 'pro',
    name: 'Pro Queue',
    bookingsLimit: 200,
    monthlyPrice: 199,
    sixMonthsPrice: 995,
    yearlyPrice: 1990,
    features: [
      '200 การจองต่อเดือน',
      'Dashboard Notification',
      'ตั้งค่าเวลาทำงาน',
      'จัดการคิวขั้นสูง',
      'ประวัติการจอง 60 วัน',
      'สถิติและรายงาน'
    ],
    recommended: true
  },
  {
    id: 'unlimited',
    name: 'Unlimited Queue',
    bookingsLimit: -1,
    monthlyPrice: 349,
    sixMonthsPrice: 1745,
    yearlyPrice: 3490,
    features: [
      'การจองไม่จำกัด',
      'Dashboard Notification',
      'ตั้งค่าเวลาทำงานแบบละเอียด',
      'จัดการคิวขั้นสูง',
      'ประวัติการจอง 90 วัน',
      'สถิติและรายงานแบบละเอียด',
      'Priority Support'
    ]
  }
];

export default function BookingAddonCheckout() {
  const { user } = useAuthContext();
  const { subscription, loading: salePageLoading } = useSalePage();
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState<BookingTier>('pro');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [showQRPayment, setShowQRPayment] = useState(false);
  const [wantsNotificationAddon, setWantsNotificationAddon] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/salepage/booking-addon-checkout');
      return;
    }

    if (subscription?.plan !== 'premium') {
      alert('ระบบจองคิวใช้ได้เฉพาะผู้ใช้งานแพ็คเก็จ Premium เท่านั้น');
      navigate('/salepage/renew');
      return;
    }

    checkExistingBookingAddon();
  }, [user, subscription, navigate]);

  const checkExistingBookingAddon = async () => {
    if (!user?.uid) return;

    try {
      const q = query(
        collection(db, 'bookingAddons'),
        where('userId', '==', user.uid),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        alert('คุณมีระบบจองคิวอยู่แล้ว กำลังพาไปหน้าจัดการ...');
        navigate('/salepage/booking-management');
        return;
      }

      if (subscription?.billingCycle === 'yearly') {
        setBillingCycle('yearly');
      } else if (subscription?.billingCycle === '6months') {
        setBillingCycle('6months');
      }
    } catch (error) {
      console.error('Error checking existing addon:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (tier: PricingTier) => {
    if (billingCycle === 'monthly') return tier.monthlyPrice;
    if (billingCycle === '6months') return tier.sixMonthsPrice;
    return tier.yearlyPrice;
  };

  const getNotificationAddonPrice = () => {
    if (billingCycle === 'monthly') return 49;
    if (billingCycle === '6months') return 245;
    return 490;
  };

  const getTotalPrice = () => {
    const selectedTierData = pricingTiers.find(t => t.id === selectedTier)!;
    let total = getPrice(selectedTierData);
    
    if (billingCycle !== 'yearly' && wantsNotificationAddon) {
      total += getNotificationAddonPrice();
    }
    
    return total;
  };

  const getSavings = (tier: PricingTier) => {
    if (billingCycle === 'monthly') return 0;
    const monthlyTotal = tier.monthlyPrice * (billingCycle === '6months' ? 6 : 12);
    return monthlyTotal - getPrice(tier);
  };

  const handleProceedToPayment = () => {
    setShowQRPayment(true);
  };

  const handlePaymentSuccess = async (chargeId: string) => {
    try {
      const selectedTierData = pricingTiers.find(t => t.id === selectedTier)!;
      
      const paymentData = {
        userId: user!.uid,
        userEmail: user!.email,
        type: 'booking_addon',
        tier: selectedTier,
        tierName: selectedTierData.name,
        bookingsLimit: selectedTierData.bookingsLimit,
        amount: getTotalPrice(),
        billingCycle: billingCycle,
        hasNotificationAddon: billingCycle !== 'yearly' && wantsNotificationAddon,
        notificationAddonPrice: billingCycle !== 'yearly' && wantsNotificationAddon ? getNotificationAddonPrice() : 0,
        paymentMethod: 'promptpay',
        chargeId: chargeId,
        status: 'pending',
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'bookingAddonPayments'), paymentData);

      navigate('/salepage/payment-success?type=booking_addon');
    } catch (error) {
      console.error('Error saving payment:', error);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูลการชำระเงิน');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F8F8] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (showQRPayment) {
    return (
      <div className="min-h-screen bg-[#F3F8F8]">
        <SalePageNavbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <QRPromptPayPayment
            amount={getTotalPrice()}
            planName={`${pricingTiers.find(t => t.id === selectedTier)?.name} - ${billingCycle === 'monthly' ? 'รายเดือน' : billingCycle === '6months' ? '6 เดือน' : 'รายปี'}`}
            onSuccess={handlePaymentSuccess}
            onCancel={() => setShowQRPayment(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F8F8]">
      <SalePageNavbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          กลับ
        </button>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-3">
            <Calendar className="w-10 h-10 text-purple-600" />
            เลือกแพ็คเก็จระบบจองคิว
          </h1>
          <p className="text-gray-600 text-lg">
            เพิ่มระบบจองคิวให้กับ Sale Page ของคุณ
          </p>
        </div>

        {/* Current Subscription Info */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl shadow-lg p-6 mb-8 text-white">
          <div className="flex items-center gap-4">
            <Crown className="w-8 h-8" />
            <div className="flex-1">
              <h3 className="text-xl font-bold mb-1">แพ็คเก็จปัจจุบัน: Premium</h3>
              <p className="text-white/90">
                Billing Cycle: {subscription?.billingCycle === 'yearly' ? 'รายปี' : subscription?.billingCycle === '6months' ? '6 เดือน' : 'รายเดือน'}
              </p>
              <p className="text-sm text-white/80 mt-2">
                ระบบจองคิวจะผูกกับแพ็คเก็จ Premium อัตโนมัติ
              </p>
            </div>
          </div>
        </div>

        {/* Billing Cycle Selector */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">เลือกระยะเวลาการชำระเงิน</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`p-4 rounded-xl border-2 transition-all ${
                billingCycle === 'monthly'
                  ? 'border-purple-600 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="font-bold text-gray-900">รายเดือน</div>
              <div className="text-sm text-gray-600">ชำระทุกเดือน</div>
            </button>
            <button
              onClick={() => setBillingCycle('6months')}
              className={`p-4 rounded-xl border-2 transition-all relative ${
                billingCycle === '6months'
                  ? 'border-purple-600 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                ฟรี 1 เดือน
              </div>
              <div className="font-bold text-gray-900">6 เดือน</div>
              <div className="text-sm text-gray-600">จ่าย 5 เดือน</div>
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`p-4 rounded-xl border-2 transition-all relative ${
                billingCycle === 'yearly'
                  ? 'border-purple-600 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="absolute -top-2 -right-2 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                ฟรี 2 เดือน
              </div>
              <div className="font-bold text-gray-900">รายปี</div>
              <div className="text-sm text-gray-600">จ่าย 10 เดือน</div>
            </button>
          </div>
        </div>

        {/* Pricing Tiers */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {pricingTiers.map((tier) => (
            <div
              key={tier.id}
              className={`bg-white rounded-2xl shadow-lg p-6 border-2 transition-all cursor-pointer ${
                selectedTier === tier.id
                  ? 'border-purple-600 ring-4 ring-purple-100'
                  : 'border-gray-200 hover:border-purple-300'
              } ${tier.recommended ? 'relative' : ''}`}
              onClick={() => setSelectedTier(tier.id)}
            >
              {tier.recommended && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                  แนะนำ
                </div>
              )}
              
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                <div className="text-4xl font-bold text-purple-600 mb-1">
                  ฿{getPrice(tier).toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">
                  {billingCycle === 'monthly' ? 'ต่อเดือน' : billingCycle === '6months' ? 'ทุก 6 เดือน' : 'ต่อปี'}
                </div>
                {getSavings(tier) > 0 && (
                  <div className="text-sm text-green-600 font-semibold mt-1">
                    ประหยัด ฿{getSavings(tier).toLocaleString()}!
                  </div>
                )}
              </div>

              <div className="space-y-3 mb-6">
                {tier.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setSelectedTier(tier.id)}
                className={`w-full py-3 rounded-xl font-bold transition-all ${
                  selectedTier === tier.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {selectedTier === tier.id ? 'เลือกแล้ว' : 'เลือกแพ็คเก็จนี้'}
              </button>
            </div>
          ))}
        </div>

        {/* Notification Add-on (for non-yearly) */}
        {billingCycle !== 'yearly' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <Bell className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  เพิ่ม Email + LINE Notification
                </h3>
                <p className="text-gray-600 mb-4">
                  รับการแจ้งเตือนทาง Email และ LINE เมื่อมีคนจองคิว
                </p>
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-gray-900">Email Notification</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-gray-900">LINE Notify</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      +฿{getNotificationAddonPrice().toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">
                      {billingCycle === 'monthly' ? 'ต่อเดือน' : 'ทุก 6 เดือน'}
                    </div>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <span className="text-gray-700 font-medium">เพิ่ม Add-on นี้</span>
                    <input
                      type="checkbox"
                      checked={wantsNotificationAddon}
                      onChange={(e) => setWantsNotificationAddon(e.target.checked)}
                      className="w-6 h-6 text-purple-600 rounded focus:ring-purple-500"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Yearly Benefits Banner */}
        {billingCycle === 'yearly' && (
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl shadow-lg p-6 mb-8 text-white">
            <div className="flex items-center gap-4">
              <Sparkles className="w-8 h-8" />
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">🎁 สิทธิพิเศษสำหรับรายปี</h3>
                <div className="space-y-1 text-white/90">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    <span>Email Notification - ฟรี!</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    <span>LINE Notify - ฟรี!</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    <span>ประวัติการจอง 90 วัน</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    <span>ประหยัดกว่ารายเดือนถึง ฿{(pricingTiers.find(t => t.id === selectedTier)!.monthlyPrice * 2).toLocaleString()}/ปี</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary and Checkout */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">สรุปการชำระเงิน</h2>
          
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">
                {pricingTiers.find(t => t.id === selectedTier)?.name}
              </span>
              <span className="font-semibold text-gray-900">
                ฿{getPrice(pricingTiers.find(t => t.id === selectedTier)!).toLocaleString()}
              </span>
            </div>
            
            {billingCycle !== 'yearly' && wantsNotificationAddon && (
              <div className="flex items-center justify-between text-blue-600">
                <span>Email + LINE Notification</span>
                <span className="font-semibold">
                  +฿{getNotificationAddonPrice().toLocaleString()}
                </span>
              </div>
            )}

            {billingCycle === 'yearly' && (
              <div className="flex items-center justify-between text-green-600">
                <span>Email + LINE Notification</span>
                <span className="font-semibold">ฟรี! 🎁</span>
              </div>
            )}
          </div>

          <div className="border-t-2 border-gray-200 pt-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-gray-900">ยอดรวมทั้งหมด</span>
              <span className="text-3xl font-bold text-purple-600">
                ฿{getTotalPrice().toLocaleString()}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {billingCycle === 'monthly' ? 'ต่อเดือน' : billingCycle === '6months' ? 'ทุก 6 เดือน' : 'ต่อปี'}
            </p>
          </div>

          <button
            onClick={handleProceedToPayment}
            className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-bold text-lg shadow-lg transition-all"
          >
            ดำเนินการชำระเงิน
          </button>
          
          <p className="text-xs text-gray-500 text-center mt-3">
            คลิกปุ่มเพื่อดำเนินการชำระเงินและยอมรับเงื่อนไขการใช้บริการ
          </p>
        </div>
      </div>
    </div>
  );
}
